import * as React from "react";
import { Button, makeStyles } from "@material-ui/core";
import { BsArrowLeft, BsCheck, BsLockFill, BsPencil } from "react-icons/bs";
import EventHelper from "common/helpers/eventHelper/eventHelper";
import UserManager from "common/userManager";
import StoreKeys from "common/storeKeys";
import { connect } from "common/connect";
import { changeUrl } from "common/history";
import { GetCurrentGamePrefix } from "common/constants/Games";
import { stageUsesBracket } from "common/helpers/eventFormatHelper";
import ViewToggle from "common/components/ViewToggle/ViewToggle";
import { Colors } from "styles/Colors";

const CONTROL_HEIGHT = 34;
const CONTROL_HEIGHT_DESKTOP = 38;
// caps paint ~1px high. Need to nudge a bit.
const CAPS_NUDGE = "translateY(0.07em)";

const propKeys = {
	bracketUser: StoreKeys.EVENT.BRACKET_USER,
	hasBracket: StoreKeys.EVENT.HAS_BRACKET,
	eventData: StoreKeys.EVENT.EVENTDATA,
	format: StoreKeys.EVENT.FORMAT,
	stageIndex: StoreKeys.EVENT.STAGE_INDEX,
	userBracketPicks: StoreKeys.EVENT.USER.BRACKET_PICKS,
	userGroupPicks: StoreKeys.EVENT.USER.GROUP_PICKS,
};

const useStyles = makeStyles((theme) => {
	const desktop = theme.breakpoints.up("md");

	return {
		eventFooter: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			marginTop: 8,
			paddingBottom: 10,
		},
		row: {
			display: "flex",
			flexDirection: "row",
			flexWrap: "wrap",
			alignItems: "center",
			justifyContent: "center",
			gap: "8px 22px",
			width: "100%",
			userSelect: "none",

			[desktop]: {
				gap: "8px 24px",
			},
		},
		status: {
			display: "inline-flex",
			alignItems: "center",
			gap: 8,
			height: CONTROL_HEIGHT,
			color: Colors.primaryDark,
			cursor: "default",
			fontSize: 11,
			fontWeight: 600,
			letterSpacing: "0.18em",
			textTransform: "uppercase",

			"& svg": {
				width: 15,
				height: 15,
				flex: "0 0 auto",
			},

			[desktop]: {
				height: CONTROL_HEIGHT_DESKTOP,
				fontSize: 12,

				"& svg": {
					width: 16,
					height: 16,
				},
			},
		},
		statusLabel: {
			transform: CAPS_NUDGE,
		},
		submitted: {
			"& svg": {
				color: Colors.pickstopGreen,
			},
		},
		lockedChip: {
			padding: "0 14px",
			borderRadius: 999,
			border: "1px solid rgba(255, 255, 255, 0.16)",
			color: Colors.primaryDarker,
			boxSizing: "border-box",

			"& svg": {
				color: Colors.primaryDarker,
			},

			[desktop]: {
				padding: "0 16px",
			},
		},
		action: {
			height: CONTROL_HEIGHT,
			padding: "0 18px",
			borderRadius: 999,
			backgroundColor: Colors.primary,
			color: Colors.panel,
			fontSize: 11,
			fontWeight: 700,
			letterSpacing: "0.18em",
			transition:
				"background-color 120ms ease, transform 120ms ease, box-shadow 120ms ease",

			"&:hover": {
				backgroundColor: Colors.primaryDark,
				transform: "translateY(-1px)",
			},

			"& .MuiButton-startIcon": {
				marginRight: 8,
				marginLeft: 0,
			},

			"& svg": {
				width: 14,
				height: 14,
			},

			[desktop]: {
				height: CONTROL_HEIGHT_DESKTOP,
				padding: "0 20px",
				fontSize: 12,

				"& svg": {
					width: 15,
					height: 15,
				},
			},
		},
		toggle: {
			[desktop]: {
				height: CONTROL_HEIGHT_DESKTOP,

				"& button": {
					fontSize: 11,
					padding: "0 18px",
				},
			},
		},
	};
});

export function EventFooterC(props) {
	const {
		bracketUser,
		hasBracket,
		eventData,
		format,
		stageIndex,
		userBracketPicks,
		userGroupPicks,
		width,
		onEdit,
		showingOfficialResults,
		showOfficialResults,
		picksLocked,
		isCurrentStage,
	} = props;
	const styles = useStyles();

	const isCurrentUser =
		bracketUser == null || bracketUser.userId === UserManager.getUserId();

	const currentStage = format?.stages?.[stageIndex];
	const eventStage = eventData?.eventStages?.[stageIndex];
	const usesBracket = !!currentStage && stageUsesBracket(currentStage);
	const stagePicks = usesBracket ? userBracketPicks : userGroupPicks;
	const hasPicksForStage =
		!!eventStage &&
		(stagePicks ?? []).some((p) => p.eventStageId === eventStage.id);

	const onSelectView = (id) => {
		showOfficialResults(id === "results");
	};

	const backToMyPicks = () => {
		changeUrl(
			GetCurrentGamePrefix() + "/event/" + EventHelper.currentEventId
		);
	};

	const viewSwitch = (
		<ViewToggle
			className={styles.toggle}
			value={showingOfficialResults ? "results" : "picks"}
			items={[
				{ id: "picks", text: isCurrentUser ? "My picks" : "Their picks" },
				{ id: "results", text: "Results" },
			]}
			onChange={onSelectView}
		/>
	);

	return (
		<div style={{ width: width ?? 400 }} className={styles.eventFooter}>
			<div className={styles.row}>
				{isCurrentUser && (
					<>
						{hasPicksForStage && (
							<span className={styles.status + " " + styles.submitted}>
								<BsCheck />
								<span className={styles.statusLabel}>Submitted</span>
							</span>
						)}
						{!picksLocked && (
							<Button
								className={styles.action}
								startIcon={<BsPencil />}
								onClick={() => {
									if (onEdit) {
										onEdit();
									} else {
										console.error(
											"onEdit callback was not provided to EventFooter Component!"
										);
									}
								}}
							>
								Edit your picks
							</Button>
						)}
						{picksLocked && (
							<span className={styles.status + " " + styles.lockedChip}>
								<BsLockFill />
								<span className={styles.statusLabel}>
									{isCurrentStage ? "Picks locked" : "Stage closed"}
								</span>
							</span>
						)}
						{viewSwitch}
					</>
				)}
				{!isCurrentUser && (
					<>
						<span className={styles.status}>
							<span className={styles.statusLabel}>
								{bracketUser.username + "'s picks"}
							</span>
						</span>
						{(!picksLocked || hasBracket) && (
							<Button
								className={styles.action}
								startIcon={<BsArrowLeft />}
								onClick={backToMyPicks}
							>
								Back to my picks
							</Button>
						)}
						{/* results stay hidden until picks lock */}
						{picksLocked && viewSwitch}
					</>
				)}
			</div>
		</div>
	);
}

export default function EventFooter(props) {
	return connect(<EventFooterC />, propKeys, props);
}
